import React, {
  createContext,
  useContext,
  useEffect,
  useReducer,
  useMemo,
  useCallback
} from "react"

/* ================= CONFIG ================= */

const STORAGE_KEY = "caluba-cart-v2"
const STORAGE_VERSION = 2

/* ================= CONTEXT ================= */

const CartContext = createContext(null)

/* ================= HELPERS ================= */

const safeParse = (value) => {
  try {
    return JSON.parse(value)
  } catch {
    return null
  }
}

const makeKey = (item) =>
  `${item.id}-${JSON.stringify(item.options || {})}`

/* ================= STATE ================= */

const initialState = {
  items: [],
  hydrated: false
}

/* ================= REDUCER ================= */

function reducer(state, action) {
  switch (action.type) {

    case "HYDRATE": {
      return {
        ...state,
        items: action.payload || [],
        hydrated: true
      }
    }

    case "ADD": {
      const item = action.payload
      const key = makeKey(item)

      const existing = state.items.find(i => i._key === key)

      let items

      if (existing) {
        items = state.items.map(i =>
          i._key === key
            ? { ...i, quantity: i.quantity + 1 }
            : i
        )
      } else {
        items = [
          ...state.items,
          {
            ...item,
            quantity: 1,
            _key: key
          }
        ]
      }

      return { ...state, items }
    }

    case "SET_QTY": {
      const { key, quantity } = action.payload

      return {
        ...state,
        items: state.items.map(item =>
          item._key === key
            ? { ...item, quantity: Math.max(1, quantity) }
            : item
        )
      }
    }

    case "REMOVE":
      return {
        ...state,
        items: state.items.filter(i => i._key !== action.payload)
      }

    case "CLEAR":
      return { ...state, items: [] }

    case "INC":
      return {
        ...state,
        items: state.items.map(i =>
          i._key === action.payload
            ? { ...i, quantity: i.quantity + 1 }
            : i
        )
      }

    case "DEC": {
      const key = action.payload

      const item = state.items.find(i => i._key === key)
      if (!item) return state

      const newQty = (item.quantity || 1) - 1

      if (newQty <= 0) {
        return {
          ...state,
          items: state.items.filter(i => i._key !== key)
        }
      }

      return {
        ...state,
        items: state.items.map(i =>
          i._key === key
            ? { ...i, quantity: newQty }
            : i
        )
      }
    }

    default:
      return state
  }
}

/* ================= PROVIDER ================= */

export const CartProvider = ({ children }) => {
  const [state, dispatch] = useReducer(reducer, initialState)

  /* HYDRATE */
  useEffect(() => {
    if (typeof window === "undefined") return

    const raw = localStorage.getItem(STORAGE_KEY)
    const parsed = safeParse(raw)

    dispatch({
      type: "HYDRATE",
      payload: parsed?.items || []
    })
  }, [])

  /* PERSIST */
  useEffect(() => {
    if (!state.hydrated) return
    if (typeof window === "undefined") return

    localStorage.setItem(
      STORAGE_KEY,
      JSON.stringify({
        version: STORAGE_VERSION,
        items: state.items
      })
    )
  }, [state.items, state.hydrated])

  /* ACTIONS */
  const addItem = useCallback((item) => {
    dispatch({ type: "ADD", payload: item })
  }, [])

  const setQty = useCallback((key, quantity) => {
    dispatch({ type: "SET_QTY", payload: { key, quantity } })
  }, [])

  const removeItem = useCallback((key) => {
    dispatch({ type: "REMOVE", payload: key })
  }, [])

  const clearCart = useCallback(() => {
    dispatch({ type: "CLEAR" })
  }, [])

  const increment = useCallback((key) => {
    dispatch({ type: "INC", payload: key })
  }, [])

  const decrement = useCallback((key) => {
    dispatch({ type: "DEC", payload: key })
  }, [])

  /* SELECTORS */
  const totalItems = useMemo(
    () => state.items.reduce((a, b) => a + b.quantity, 0),
    [state.items]
  )

  const totalPrice = useMemo(
    () => state.items.reduce((a, b) => a + b.price * b.quantity, 0),
    [state.items]
  )

  const value = useMemo(() => ({
    items: state.items,
    hydrated: state.hydrated,

    addItem,
    setQty,
    removeItem,
    clearCart,
    increment,
    decrement,

    totalItems,
    totalPrice
  }), [
    state.items,
    state.hydrated,
    addItem,
    removeItem,
    clearCart,
    increment,
    decrement,
    totalItems,
    totalPrice
  ])

  return (
    <CartContext.Provider value={value}>
      {children}
    </CartContext.Provider>
  )
}

/* ================= HOOK ================= */

export const useCart = () => {
  const ctx = useContext(CartContext)
  if (!ctx) throw new Error("useCart must be used inside CartProvider")
  return ctx
}